/**
 * The arithmetic and shaping behind the super-admin voice page.
 *
 * Everything here is pure: dates in, dates out; the caller supplies `now`. The
 * page (`router-pages/loggedin/VoiceAdmin.vue`) does the rendering and the
 * fetching, and the store does the transport — so what is left in this file is
 * exactly the part that can be wrong in a way no screenshot would reveal, and
 * that is the part `scripts/test-voice-admin.js` drives directly.
 *
 * Two rules run through all of it.
 *
 * ABSENT IS NOT ZERO. `VoiceCredits.vue` learned this the hard way: rendering a
 * missing figure as a confident "0" invents a fact. The overview contract
 * (FIX-B6 item 7) promises zeros rather than nulls for the counters on an empty
 * range, so those are coerced to 0 — but the latency percentiles are NOT, since
 * "no parse took any time" and "we have not measured a parse" are different
 * statements and `p50ParseMs: null` must stay an em dash on the screen.
 *
 * NEVER DIVIDE BLIND. Every percentage here has a denominator that is legitimately
 * zero on a quiet day — no commands, no parses, no cost. A funnel that renders
 * `NaN%` on the first day of a beta is worse than one that renders nothing, so
 * each ratio returns 0 when its denominator is 0 rather than letting IEEE decide.
 */

// The dock's own span list. Imported rather than retyped: the page that ranks a
// stage and the dock that measures it must agree on the key to the character,
// or the stage aggregates into nothing on a page that still renders.
import { MAX_SPAN_MS, SPANS, SPAN_SEPARATOR } from "./voiceTiming.js";

// ---------------------------------------------------------------------------
// Range presets
// ---------------------------------------------------------------------------

/**
 * The presets, in the order the picker shows them.
 *
 * `days` counts CALENDAR days including today, not 24-hour blocks: "7 days" is
 * midnight six days ago until now. A rolling 168 hours would move the left edge
 * every time the page was refreshed, so two reads of "the same" week would
 * disagree — and an admin comparing this week with last week would be comparing
 * two different windows.
 */
export const RANGE_PRESETS = Object.freeze([
  { key: "today", label: "Today", days: 1 },
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: 30 },
  { key: "90d", label: "90 days", days: 90 },
  { key: "custom", label: "Custom", days: 0 },
]);

/** The backend's own default (FIX-B6 item 7), so a first load matches it. */
export const DEFAULT_PRESET = "30d";

function startOfDay(date) {
  const start = new Date(date.getTime());
  start.setHours(0, 0, 0, 0);
  return start;
}

function endOfDay(date) {
  const end = new Date(date.getTime());
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Local midnight `days - 1` days before `date`.
 *
 * `setDate` is deliberate: subtracting `n * 86400000` milliseconds lands an hour
 * early or late across a daylight-saving boundary, which would silently shift
 * the edge of every range spanning one.
 */
function startOfDaysAgo(date, days) {
  const start = startOfDay(date);
  start.setDate(start.getDate() - (Math.max(1, days) - 1));
  start.setHours(0, 0, 0, 0);
  return start;
}

/** A `YYYY-MM-DD` value from `<input type="date">` as a LOCAL date, or null. */
function parseDateInput(value) {
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const [, y, m, d] = match;
  // Built field by field rather than `new Date(value)`, because the string form
  // of an ISO date is parsed as UTC and would be the previous day west of
  // Greenwich — the range would start a day early for exactly half the world.
  const date = new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);
  if (Number.isNaN(date.getTime())) return null;
  if (date.getMonth() !== Number(m) - 1 || date.getDate() !== Number(d))
    return null; // 2026-02-31 and friends
  return date;
}

/**
 * Turn the picker's state into the `since`/`until` the endpoints take.
 *
 * `pending` is the third answer, and it is why this does not just return a
 * range: "Custom" with only one date filled in is not a range yet, and firing a
 * request for the fallback window would quietly show the user numbers for a
 * period they did not ask for. The page keeps what is on screen and waits.
 *
 * @param {{preset?: string, from?: string, to?: string}} state
 * @param {Date} [now]
 * @returns {{since: string, until: string, preset: string, pending: boolean}}
 */
export function rangeFor(state, now) {
  const at = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  const preset = (state && state.preset) || DEFAULT_PRESET;

  if (preset === "custom") {
    let from = parseDateInput(state && state.from);
    let to = parseDateInput(state && state.to);
    if (!from || !to) {
      const fallback = presetRange(DEFAULT_PRESET, at);
      return { ...fallback, preset: "custom", pending: true };
    }
    // Backwards is a slip, not an error worth a message: nobody means "from
    // Friday to Monday" as an empty set.
    if (from.getTime() > to.getTime()) [from, to] = [to, from];
    return {
      since: startOfDay(from).toISOString(),
      // The whole of the closing day, not its first instant — otherwise
      // picking today for both ends asks for a window of zero width.
      until: endOfDay(to).toISOString(),
      preset: "custom",
      pending: false,
    };
  }

  return { ...presetRange(preset, at), pending: false };
}

/** One named preset as an absolute range. Unknown names fall back to 30 days. */
export function presetRange(preset, now) {
  const at = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  const found =
    RANGE_PRESETS.find((p) => p.key === preset && p.key !== "custom") ||
    RANGE_PRESETS.find((p) => p.key === DEFAULT_PRESET);
  return {
    since: startOfDaysAgo(at, found.days).toISOString(),
    // `now`, not the end of today: an admin reading the page at noon should not
    // be told the window runs until midnight, and the server would count
    // nothing in the remaining hours anyway.
    until: at.toISOString(),
    preset: found.key,
  };
}

/** The label the picker and the heading share, so they cannot disagree. */
export function rangeLabel(range) {
  const preset = RANGE_PRESETS.find((p) => p.key === (range && range.preset));
  return preset ? preset.label : "";
}

// ---------------------------------------------------------------------------
// Totals
// ---------------------------------------------------------------------------

export const COST_ARMS = Object.freeze([
  { key: "muse", label: "Speech" },
  { key: "gemini", label: "Gemini" },
  { key: "codex", label: "Codex" },
  { key: "jev", label: "Jev" },
  { key: "tts", label: "Read-back" },
]);

export const PARSE_SOURCES = Object.freeze([
  { key: "grammar", label: "Grammar" },
  { key: "jev", label: "Jev" },
  { key: "gemini", label: "Gemini" },
  { key: "codex", label: "Codex" },
]);

export const TURN_KINDS = Object.freeze([
  { key: "command", label: "Command" },
  { key: "confirmation", label: "Confirmation" },
  { key: "answer", label: "Answer" },
  { key: "edit", label: "Edit" },
]);

/**
 * What a parse DECIDED (FIX-B8), as the turn log's `parse.outcome`.
 *
 * Five values, and only one of them means the sentence was understood. They are
 * listed here so the turns filter can offer them by name — an admin hunting
 * coverage gaps wants UNSUPPORTED, and an admin hunting safety wants DENIED,
 * and neither of those is findable by reading transcripts.
 */
export const PARSE_OUTCOMES = Object.freeze([
  { key: "PROPOSAL", label: "Understood" },
  { key: "CANNOT_PARSE", label: "Could not read it" },
  { key: "NEEDS_CLARIFICATION", label: "Asked a question" },
  { key: "UNSUPPORTED", label: "Outside what we do" },
  { key: "DENIED", label: "Refused" },
]);

/**
 * The abstention codes (FIX-B9 item 1), with a sentence a reader can act on.
 *
 * The LABELS are the point. "UNCLAIMED_NUMBER" is the grammar's vocabulary and
 * says nothing to somebody deciding what to build next; "a number nothing
 * explained" says the sentence had a 6 in it that no slot wanted, which is a
 * gap with an obvious fix. The codes are shown as well, because they are what a
 * backend engineer greps for.
 *
 * An unknown code is NEVER dropped. The server owns this enum and will add to
 * it; a page that rendered only the codes it knew would quietly hide the newest
 * failure mode, which is the one worth seeing.
 */
export const ABSTAIN_CODES = Object.freeze([
  { key: "UNKNOWN_VERB", label: "No verb we know" },
  { key: "UNKNOWN_NAME", label: "A name we could not place" },
  { key: "UNCLAIMED_NUMBER", label: "A number nothing explained" },
  { key: "UNCLAIMED_IDENTIFIER", label: "An identifier nothing explained" },
  { key: "ROLE_ATTACHMENT", label: "Could not tell which slot a word filled" },
  { key: "CORRECTION_SCOPE", label: "A correction we could not scope" },
  { key: "MISSING_SOURCE_CONTEXT", label: "No source to act on" },
  { key: "MISSING_DESTINATION", label: "No destination" },
  { key: "MULTIPLE_COMMANDS", label: "More than one command" },
  { key: "AMBIGUOUS_TITLE", label: "A title matching several boxes" },
  { key: "INVALID_WELL", label: "A well outside 1-12" },
  { key: "NEGATED", label: "Told us NOT to" },
  { key: "CONDITIONAL", label: "A condition, not a command" },
  { key: "QUESTION_FORM", label: "Asked rather than told" },
  { key: "NOT_A_COMMAND", label: "Not about inventory" },
  { key: "INJECTION", label: "Talking to the model" },
  { key: "JEV_UNSURE", label: "Jev was not sure enough" },
  { key: "JEV_TIMEOUT", label: "Jev ran out of time" },
  { key: "JEV_BUDGET", label: "Jev was out of budget" },
  { key: "RACE_TIMEOUT", label: "Both models ran out of time" },
  { key: "GUARD_REJECT", label: "The guard rejected the answer" },
  { key: "OTHER", label: "Unmapped" },
]);

/**
 * Every arm that can be consulted on a turn, in the order they are tried.
 *
 * Wider than `PARSE_SOURCES`, which is only the arms that can WIN. `guard` never
 * wins anything — it rejects — and a turn where the guard was consulted twice as
 * often as anything won is a turn shape worth knowing about.
 */
export const PARSE_ARMS = Object.freeze([
  { key: "grammar", label: "Grammar" },
  { key: "guard", label: "Guard" },
  { key: "jev", label: "Jev" },
  { key: "gemini", label: "Gemini" },
  { key: "codex", label: "Codex" },
]);

/**
 * The client's timing stages, in plain English (FIX-B9 item 3, FIX-F10).
 *
 * The KEYS come from `voiceTiming.js` rather than being retyped, so the dock
 * that measures a span and the page that shows it cannot drift apart: a key
 * spelled differently in two files is a stage that silently aggregates to
 * nothing on a page that still renders.
 *
 * The labels name what the person waiting experienced, not what the system was
 * doing. "sttFinal→proposal" is a stage of a pipeline; "Transcript to command"
 * is a wait somebody sat through, and the card exists so that somebody's wait
 * is the thing being ranked.
 */
/**
 * Each mark as a noun phrase that reads correctly on EITHER side of the arrow.
 *
 * Six of the thirteen marks end one stage and begin the next — `sttFinal` is
 * the end of recognition and the start of the parse — so a label written as the
 * end of a stage ("the transcript arrived") is unreadable at the front of the
 * next one. They are all phrased as moments instead, and only the composed
 * label's first letter is raised.
 */
const STAGE_LABELS = {
  captureRequested: "microphone requested",
  captureLive: "microphone live",
  speechEnd: "speech ends",
  sttFinal: "transcript",
  proposal: "command understood",
  previewVisible: "card on screen",
  ttsFirstAudible: "read-back starts",
  ttsEnded: "read-back ends",
  confirmSpeechStart: "confirm spoken",
  confirmFinal: "confirm heard",
  confirm: "confirmed",
  applied: "applied",
  receiptVisible: "receipt on screen",
};

function sentenceCase(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export const TIMING_STAGES = Object.freeze(
  SPANS.map(([from, to]) => ({
    key: `${from}${SPAN_SEPARATOR}${to}`,
    label: sentenceCase(
      `${STAGE_LABELS[from] || from} \u2192 ${STAGE_LABELS[to] || to}`
    ),
  }))
);

/**
 * A plain object that is really there.
 *
 * PRESENCE, not content, and the distinction has teeth on this page: an empty
 * `abstainsByCode: {}` is a server that HAS the feature and saw no abstention
 * in this range, while a missing one is a server that predates the feature. The
 * first is a fact about the lab and the second is a fact about the deploy, and
 * a card that rendered them identically would tell a reader the grammar never
 * gives up.
 */
function presentMap(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/** A finite number, or 0. For counters the contract promises are never null. */
function count(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** A finite number, or null. For measurements that may genuinely be unknown. */
function measure(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function countMap(raw, keys) {
  const out = {};
  for (const key of keys) out[key] = count(raw && raw[key]);
  // A source or an arm the server added since this file was written is kept
  // rather than dropped: an unnamed cost is still a cost, and the page shows it.
  if (raw && typeof raw === "object")
    for (const key of Object.keys(raw))
      if (!(key in out)) out[key] = count(raw[key]);
  return out;
}

/** The `T` of the FIX-B6 item 7 contract, with every field present. */
export function emptyTotals() {
  return {
    sessions: 0,
    audioSeconds: 0,
    turns: 0,
    commands: 0,
    previews: 0,
    applies: 0,
    refusals: 0,
    edits: 0,
    costUsd: 0,
    costByArm: countMap(null, COST_ARMS.map((a) => a.key)),
    parsesBySource: countMap(null, PARSE_SOURCES.map((s) => s.key)),
    refusalsByCode: {},
    // NULL, not `{}`, and for the same reason the percentiles beside them are
    // null: `{}` is the server saying "nothing abstained in this range" and
    // null is "nobody has told us". The cards render those two differently, so
    // an empty payload must not answer the question the server has not.
    abstainsByCode: null,
    armsBySource: null,
    p50ParseMs: null,
    p90ParseMs: null,
  };
}

/**
 * Coerce whatever arrived into the contract shape.
 *
 * The percentiles go through `measure` and everything else through `count`,
 * which is the "absent is not zero" rule made concrete: an omitted `applies` is
 * genuinely none applied, while an omitted `p50ParseMs` means nobody measured.
 */
export function normaliseTotals(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const refusalsByCode = {};
  if (source.refusalsByCode && typeof source.refusalsByCode === "object")
    for (const [code, n] of Object.entries(source.refusalsByCode))
      refusalsByCode[code] = count(n);

  return {
    sessions: count(source.sessions),
    audioSeconds: count(source.audioSeconds),
    turns: count(source.turns),
    commands: count(source.commands),
    previews: count(source.previews),
    applies: count(source.applies),
    refusals: count(source.refusals),
    edits: count(source.edits),
    costUsd: count(source.costUsd),
    costByArm: countMap(source.costByArm, COST_ARMS.map((a) => a.key)),
    parsesBySource: countMap(source.parsesBySource, PARSE_SOURCES.map((s) => s.key)),
    refusalsByCode,
    // FIX-B9 items 1 and 4. These are part of the ROW shape, not of the
    // envelope: they arrive on `totals`, on every lab row and on every user
    // row, and the rows sum to the totals the way every other counter does.
    // Kept whole rather than coerced key by key, because both enums live on the
    // server and a code this file has not heard of is the one worth seeing.
    abstainsByCode: presentMap(source.abstainsByCode) ? { ...source.abstainsByCode } : null,
    armsBySource: presentMap(source.armsBySource) ? { ...source.armsBySource } : null,
    p50ParseMs: measure(source.p50ParseMs),
    p90ParseMs: measure(source.p90ParseMs),
  };
}

/** A ratio in percent, or 0 when there is nothing to divide by. */
export function ratio(part, whole) {
  const top = Number(part);
  const bottom = Number(whole);
  if (!Number.isFinite(top) || !Number.isFinite(bottom) || bottom === 0) return 0;
  return round1((top / bottom) * 100);
}

function round1(n) {
  return Math.round(n * 10) / 10;
}

function clampPct(n) {
  return Math.max(0, Math.min(100, Number(n) || 0));
}

// ---------------------------------------------------------------------------
// Funnel, cost split, source mix
// ---------------------------------------------------------------------------

/**
 * commands -> previews -> applies.
 *
 * `pctOfFirst` is what the bar is drawn from and `pctOfPrev` is the step loss,
 * because the two answer different questions: "how much of what was said got
 * done" and "where did it fall over". Both are honest numbers — a stage larger
 * than the one before it (previews that were never recorded, say) reports past
 * 100 rather than being clipped into looking correct. Only `width`, which is
 * geometry and not a fact, is clamped.
 */
export function funnelStages(totals) {
  const t = normaliseTotals(totals);
  const stages = [
    { key: "commands", label: "Commands", count: t.commands },
    { key: "previews", label: "Previewed", count: t.previews },
    { key: "applies", label: "Applied", count: t.applies },
  ];
  const first = stages[0].count;
  return stages.map((stage, i) => {
    const prev = i === 0 ? stage.count : stages[i - 1].count;
    const pctOfFirst = ratio(stage.count, first);
    return {
      ...stage,
      pctOfFirst,
      pctOfPrev: i === 0 ? (first === 0 ? 0 : 100) : ratio(stage.count, prev),
      dropped: Math.max(0, prev - stage.count),
      width: clampPct(pctOfFirst),
    };
  });
}

/**
 * The first stage that outruns the one before it, or null.
 *
 * A turn cannot be applied more often than it was said, so this is never a fact
 * about one turn's journey — it is a fact about the window. Commands applied
 * before the turn log existed have no turn to link to, and until the dock sends
 * its turn id the server falls back to matching parse arguments, which misses
 * whenever the dock's resolved identifiers differ from the parser's raw ones.
 *
 * It exists because `dropped` is clamped at zero, so in this case the ordinary
 * "n never reached a preview" note reads "0 ... 0": two true numbers that
 * together describe nothing. The page says what actually happened instead.
 *
 * @returns {{stage: string, previous: string, missing: number}|null}
 */
export function funnelGap(totals) {
  const stages = funnelStages(totals);
  for (let i = 1; i < stages.length; i += 1)
    if (stages[i].count > stages[i - 1].count)
      return {
        stage: stages[i].label,
        previous: stages[i - 1].label,
        missing: stages[i].count - stages[i - 1].count,
      };
  return null;
}

/**
 * The by-arm cost split.
 *
 * The denominator is the LARGER of the reported total and the sum of the arms.
 * They should be equal; when they are not, dividing by the smaller would print
 * percentages over 100 and dividing by `costUsd` alone would hide an arm the
 * total forgot. The larger keeps every slice inside the bar and keeps the
 * missing money visible as the gap.
 */
export function costArms(totals) {
  const t = normaliseTotals(totals);
  const keys = Object.keys(t.costByArm);
  const sum = keys.reduce((acc, key) => acc + t.costByArm[key], 0);
  const total = Math.max(count(t.costUsd), sum);
  const labelFor = (key) =>
    (COST_ARMS.find((a) => a.key === key) || {}).label || key;

  return keys.map((key) => ({
    key,
    label: labelFor(key),
    usd: t.costByArm[key],
    pct: ratio(t.costByArm[key], total),
  }));
}

/** The stacked bar: one segment per parse source, widths summing to <= 100. */
export function sourceSegments(totals) {
  const t = normaliseTotals(totals);
  const keys = Object.keys(t.parsesBySource);
  const total = keys.reduce((acc, key) => acc + t.parsesBySource[key], 0);
  const labelFor = (key) =>
    (PARSE_SOURCES.find((s) => s.key === key) || {}).label || key;

  return keys.map((key) => ({
    key,
    label: labelFor(key),
    count: t.parsesBySource[key],
    pct: ratio(t.parsesBySource[key], total),
    width: clampPct(ratio(t.parsesBySource[key], total)),
  }));
}

// ---------------------------------------------------------------------------
// Where the time goes, and why the grammar gave up (FIX-B9 items 1, 3 and 4)
// ---------------------------------------------------------------------------

/**
 * `{ p50, p95, n }` for one stage, coerced.
 *
 * The percentiles go through `measure` and `n` through `count`, which is the
 * "absent is not zero" rule with real consequences here: a stage nobody
 * measured must print an em dash, because a confident `0 ms` next to a stage
 * that takes six seconds reads as "this part is instant" — the exact wrong
 * conclusion, drawn from no data at all.
 */
function normaliseStage(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  return {
    p50: measure(source.p50),
    p95: measure(source.p95),
    n: count(source.n),
  };
}

/**
 * Whether the server answered with a block at all, wherever it keeps it.
 *
 * `stages` sits on the ENVELOPE, because a percentile does not fold: there is
 * no meaningful per-lab p95 to sum, so the server publishes one set for the
 * range. `abstainsByCode` and `armsBySource` are part of the ROW shape and
 * therefore arrive on `totals`, on every lab and on every user, summing the way
 * the counters beside them do.
 *
 * Both places are read for the two row blocks, envelope first being wrong and
 * row first being right. The fallback is not indecision: this page has to stay
 * readable against a server mid-deploy, and `turnsFrom` already establishes
 * that guessing one envelope and finding nothing renders an empty card that
 * reports no error, which is the most expensive kind of wrong.
 */
function blockAt(payload, key) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.totals && presentMap(payload.totals[key])) return payload.totals[key];
  if (presentMap(payload[key])) return payload[key];
  return null;
}

/** True when the block was answered at all, at either address. */
function hasBlock(payload, key) {
  if (!payload || typeof payload !== "object") return false;
  if (payload.totals && presentMap(payload.totals[key])) return true;
  return presentMap(payload[key]);
}

function present(payload, key) {
  return !!payload && typeof payload === "object" && presentMap(payload[key]);
}

/**
 * One row per stage, ranked by p95, on a SHARED horizontal scale.
 *
 * P95, not p50, decides the order. The stage that makes voice feel slow is the
 * one with the bad tail — a read-back whose median is 4 s and whose p95 is 4.2 s
 * is working as designed, and a parse whose median is 40 ms and whose p95 is
 * 11 s is the one people complain about. Sorting by the median would put that
 * parse last.
 *
 * ONE SCALE for every bar, taken from the largest p95 on the page. Per-row
 * scaling would draw every stage as a full-width bar and make the card a list
 * of identical rectangles — the comparison IS the point, and a bar that cannot
 * be compared with the one under it is decoration.
 *
 * AND A CLAMPED ROW IS NOT ALLOWED TO SET IT. `MAX_SPAN_MS` is a ceiling, not a
 * measurement: a stage sitting on it is the ledger saying "at least two
 * minutes", which happens when a tab was backgrounded mid-turn. Seen live on
 * the dev stack, one such row took the scale to 120,000 ms and collapsed eight
 * honest stages — the longest of them a real 2.1 s — into slivers a pixel wide.
 * Letting a non-measurement decide the geometry for every measurement beside it
 * is the same failure as per-row scaling, arriving from the other direction. So
 * the scale comes from the largest p95 BELOW the ceiling, a clamped row is
 * drawn full width and flagged `atCeiling`, and the page prints it as "at
 * least" rather than as a figure.
 *
 * Unmeasured stages are kept and sorted last rather than dropped: "nobody has
 * ever heard a read-back finish" is a finding, and a row that vanishes tells
 * nobody anything.
 */
export function stageRows(overview) {
  const raw = (overview && overview.stages) || {};
  const rows = TIMING_STAGES.map((stage, index) => ({
    ...stage,
    ...normaliseStage(raw[stage.key]),
    index,
  }));
  // Keys the server added since this file was written are kept, exactly as an
  // unnamed cost arm is: an unlabelled stage is still time somebody waited.
  for (const key of Object.keys(raw))
    if (!rows.some((row) => row.key === key))
      rows.push({
        key,
        label: key,
        ...normaliseStage(raw[key]),
        index: rows.length,
      });

  const atCeiling = (value) => value !== null && value >= MAX_SPAN_MS;
  const scale = rows.reduce(
    (top, row) =>
      row.p95 !== null && !atCeiling(row.p95) && row.p95 > top ? row.p95 : top,
    0
  );
  const width = (value) => {
    if (value === null) return 0;
    // Everything is clamped, so there is nothing honest left to scale against.
    // Full width is then the only truthful drawing: every row says "at least".
    if (scale <= 0) return atCeiling(value) ? 100 : 0;
    return clampPct((value / scale) * 100);
  };

  return rows
    .map((row) => ({
      ...row,
      measured: row.n > 0 && (row.p50 !== null || row.p95 !== null),
      // The bar runs off the end of the card, and the page says so in words.
      atCeiling: atCeiling(row.p95),
      width50: width(row.p50),
      width95: width(row.p95),
    }))
    .sort((a, b) => {
      if (a.measured !== b.measured) return a.measured ? -1 : 1;
      if (a.measured && b.measured) {
        const byP95 = (b.p95 || 0) - (a.p95 || 0);
        if (byP95) return byP95;
      }
      // Ties fall back to the order a turn walks through them, so two stages
      // that take the same time still read as a sequence.
      return a.index - b.index;
    });
}

/** True when the server sent a stage block at all. */
export function hasStages(payload) {
  return present(payload, "stages");
}

/**
 * Abstention codes, commonest first: the queue of what to teach the grammar.
 *
 * An unknown code keeps its own name as its label. The enum lives on the server
 * and will grow, and the newest code is by definition the one nobody has looked
 * at yet — dropping it, or printing it as "Other", would hide the only row on
 * this card that is news.
 */
export function abstainRows(overview) {
  const raw = blockAt(overview, "abstainsByCode") || {};
  const total = Object.values(raw).reduce((acc, n) => acc + count(n), 0);
  return Object.entries(raw)
    .map(([code, n]) => {
      const known = ABSTAIN_CODES.find((c) => c.key === code);
      return {
        code,
        label: known ? known.label : code,
        known: !!known,
        count: count(n),
        pct: ratio(count(n), total),
        width: clampPct(ratio(count(n), total)),
      };
    })
    .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
}

/** True when the server sent the abstain block at all. */
export function hasAbstains(payload) {
  return hasBlock(payload, "abstainsByCode");
}

/**
 * How often each arm was CONSULTED against how often it won.
 *
 * Two numbers rather than one because they answer different questions. "Jev
 * won 143 parses" is a success figure; "Jev was asked 412 times and won 143"
 * is a cost figure, and the arms are billed on being asked. A ratio near 1 is
 * an arm earning its calls; a ratio near 0 is an arm being paid to abstain.
 */
export function armRows(overview) {
  const raw = blockAt(overview, "armsBySource") || {};
  const keys = [
    ...PARSE_ARMS.map((a) => a.key).filter((k) => k in raw),
    ...Object.keys(raw).filter((k) => !PARSE_ARMS.some((a) => a.key === k)),
  ];
  return keys.map((key) => {
    const entry = raw[key] && typeof raw[key] === "object" ? raw[key] : {};
    const consulted = count(entry.consulted);
    const won = count(entry.won);
    const known = PARSE_ARMS.find((a) => a.key === key);
    return {
      key,
      label: known ? known.label : key,
      consulted,
      won,
      // Never divided blind: an arm that was never consulted is 0%, not NaN.
      winPct: ratio(won, consulted),
      width: clampPct(ratio(won, consulted)),
    };
  });
}

/** True when the server sent the arm block at all. */
export function hasArms(payload) {
  return hasBlock(payload, "armsBySource");
}

/**
 * The grouped "shapes" from `GET /api/v1/voice/turns?shape=1`.
 *
 * The server normalises transcripts — numbers to N, serials to P, titles to T —
 * and returns one row per distinct sentence shape with a count and one real
 * example. That turns a log nobody reads into a queue: the shape said 41 times
 * and never understood is the next thing to build, and it is invisible in a
 * list of 41 individual transcripts.
 *
 * The envelope and the field names are read at more than one spelling for the
 * same reason `shapeTurn` reads the joined names three ways: the contract
 * promises the data, not the wrapper, and guessing wrong renders an empty card
 * that reports no error — the most expensive kind of wrong.
 *
 * `abstainCodes` earns its place on the row. A shape said 41 times is a queue
 * item; a shape said 41 times that ALWAYS died on MISSING_DESTINATION is a
 * queue item with the fix written on it, and that is one click saved on every
 * row of the card. It arrives counted, so the causes are ranked rather than
 * merely listed.
 */
export function shapeGroups(payload) {
  const rows = Array.isArray(payload)
    ? payload
    : (payload && (payload.shapes || payload.data || payload.rows)) || [];
  const list = Array.isArray(rows) ? rows : [];
  const total = list.reduce((acc, row) => acc + count(row && row.count), 0);

  return list
    .map((raw) => {
      const row = raw && typeof raw === "object" ? raw : {};
      // NOT through `pick`, which is a string helper: a numeric count would
      // come back as "" and every shape would tie at zero, leaving the card
      // sorted alphabetically and silently useless.
      const n = count(row.count === undefined ? row.n : row.count);
      return {
        shape: pick(row.shape, row.pattern, row.normalised, row.normalized),
        // Why this shape kept failing, and what the parser decided about it.
        // Filtered to strings so one malformed entry cannot take the row down,
        // and never truncated: a shape with four different causes is a shape
        // whose four causes are the finding.
        abstainCodes: codeList(row.abstainCodes),
        outcomes: codeList(row.outcomes),
        // When the example was said, for the row's title. Absent is absent.
        exampleAt: pick(row.exampleAt),
        // Verbatim, like every other transcript on this page: the example is
        // what somebody actually said, and it is what gets pasted into the
        // search box to find the rest.
        example: typeof row.example === "string"
          ? row.example
          : typeof row.sample === "string"
            ? row.sample
            : typeof row.transcript === "string"
              ? row.transcript
              : "",
        count: n,
        pct: ratio(n, total),
        width: clampPct(ratio(n, total)),
      };
    })
    .filter((row) => !!row.shape)
    .sort((a, b) => b.count - a.count || a.shape.localeCompare(b.shape));
}

/**
 * `{ CODE: n }` or `[CODE, …]` as `[{code, count}]`, commonest cause first.
 *
 * FIX-B9 ships these as COUNTED MAPS — a shape said five times that died twice
 * on UNCLAIMED_NUMBER and three times on QUESTION_FORM is two different pieces
 * of work, and which one is bigger decides which gets built. A bare list would
 * throw that away, so the count rides along and the order is by it.
 *
 * The array form is still read. It costs four lines, it is what a reasonable
 * person would have shipped, and the failure it prevents is a card that renders
 * empty while reporting no error.
 */
function codeList(value) {
  if (Array.isArray(value)) {
    const seen = new Set();
    const out = [];
    for (const entry of value) {
      const code = typeof entry === "string" ? entry.trim() : "";
      if (!code || seen.has(code)) continue;
      seen.add(code);
      // Null, not 0: the list form does not say how many, and 0 would say none.
      out.push({ code, count: null });
    }
    return out;
  }
  if (!presentMap(value)) return [];
  return Object.entries(value)
    .map(([code, n]) => ({ code: String(code).trim(), count: count(n) }))
    .filter((entry) => !!entry.code)
    .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
}

/** Refusal codes, commonest first, so the worst offender is the first line. */
export function refusalCodes(totals) {
  const t = normaliseTotals(totals);
  return Object.entries(t.refusalsByCode)
    .map(([code, n]) => ({ code, count: n, pct: ratio(n, t.refusals) }))
    .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
}

// ---------------------------------------------------------------------------
// Table rows
// ---------------------------------------------------------------------------

/**
 * A Mongo id as a string, whether it arrived as one or inside a populated
 * document. `String(object)` is not used: a plain object stringifies to
 * "[object Object]", which would then be a perfectly valid-looking filter value
 * that matches nothing.
 */
export function idOf(value) {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    if (typeof value._id === "string") return value._id;
    if (value._id && typeof value._id === "object" && typeof value._id.toString === "function")
      return value._id.toString();
  }
  return "";
}

function pick(...values) {
  for (const value of values)
    if (typeof value === "string" && value.trim()) return value.trim();
  return "";
}

/**
 * Busiest first; ties broken by name, with the nameless last.
 *
 * The same rule `sortRows` applies to a missing number: a row whose name the
 * join could not supply must not win a tie on the strength of the bracket its
 * placeholder starts with, which is what a plain `localeCompare` on
 * "(unnamed lab)" does.
 */
function byTurnsThenName(a, b) {
  if (b.turns !== a.turns) return b.turns - a.turns;
  if (a.named !== b.named) return a.named ? -1 : 1;
  return a.name.localeCompare(b.name);
}

function derived(totals) {
  return {
    audioMinutes: round1(totals.audioSeconds / 60),
    applyRate: ratio(totals.applies, totals.commands),
    refusalRate: ratio(totals.refusals, totals.turns),
  };
}

/** One row per lab, biggest user of voice first. */
export function labRows(overview) {
  const labs = (overview && Array.isArray(overview.labs) && overview.labs) || [];
  return labs
    .map((lab) => {
      const totals = normaliseTotals(lab);
      const name = pick(lab.labName, lab.name);
      return {
        key: idOf(lab.labId) || name || "lab",
        labId: idOf(lab.labId),
        // Never blank: a row with no name still has numbers, and an unlabelled
        // row is a question the admin can at least ask about.
        name: name || "(unnamed lab)",
        named: !!name,
        ...totals,
        ...derived(totals),
      };
    })
    .sort(byTurnsThenName);
}

/** One row per user. `lastAt` is the only field here the lab rows do not have. */
export function userRows(overview) {
  const users = (overview && Array.isArray(overview.users) && overview.users) || [];
  return users
    .map((user) => {
      const totals = normaliseTotals(user);
      const name = pick(user.name, user.userName);
      return {
        key: idOf(user.userId) || pick(user.email) || "user",
        userId: idOf(user.userId),
        labId: idOf(user.labId),
        name: name || "(unnamed user)",
        named: !!name,
        email: pick(user.email, user.userEmail),
        labName: pick(user.labName) || "(unnamed lab)",
        lastAt: pick(user.lastAt),
        ...totals,
        ...derived(totals),
      };
    })
    .sort(byTurnsThenName);
}

/**
 * Sort a shaped table by one column.
 *
 * Missing values sort to the END in both directions. Sorting them as 0 or as
 * the empty string would mix "no voice use" in among real numbers at whichever
 * edge the direction happened to put them, and an admin scanning for the
 * quietest lab would find rows that simply failed to report.
 */
export function sortRows(rows, key, dir) {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const sign = dir === "asc" ? 1 : -1;
  const missing = (v) => v === null || v === undefined || v === "";

  return list
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const x = a.row[key];
      const y = b.row[key];
      if (missing(x) && missing(y)) return a.index - b.index;
      if (missing(x)) return 1;
      if (missing(y)) return -1;
      if (typeof x === "number" && typeof y === "number")
        return (x - y) * sign || a.index - b.index;
      return String(x).localeCompare(String(y)) * sign || a.index - b.index;
    })
    .map((entry) => entry.row);
}

// ---------------------------------------------------------------------------
// Turn rows
// ---------------------------------------------------------------------------

const ARG_KEY_LIMIT = 6;
const ARG_VALUE_CHARS = 40;

function argValue(value) {
  if (value === null) return "null";
  if (value === undefined) return "";
  if (Array.isArray(value)) {
    const shown = value.slice(0, 3).map(argValue);
    const rest = value.length - shown.length;
    return `[${shown.join(", ")}${rest > 0 ? `, +${rest}` : ""}]`;
  }
  if (typeof value === "object") return "{…}";
  const text = String(value);
  return text.length > ARG_VALUE_CHARS
    ? `${text.slice(0, ARG_VALUE_CHARS - 1)}…`
    : text;
}

/**
 * `{well: 7, puck: "VP1"}` -> `well=7 puck=VP1`, for the collapsed row.
 *
 * Deliberately lossy and deliberately never throws: the args are the machine
 * vocabulary of a tool this file does not know the schema of, and a nested
 * object or an array from some future tool must degrade to a marker rather than
 * take the row down. The expanded row shows the real JSON, so nothing here is
 * the only copy of anything.
 */
export function compactArgs(args) {
  if (!args || typeof args !== "object" || Array.isArray(args)) return "";
  const entries = Object.entries(args).filter(([, v]) => v !== undefined);
  const shown = entries.slice(0, ARG_KEY_LIMIT);
  const rest = entries.length - shown.length;
  const text = shown.map(([key, value]) => `${key}=${argValue(value)}`).join(" ");
  return rest > 0 ? `${text} +${rest} more` : text;
}

/**
 * One turn's client spans, as `[{key, label, ms}]` in turn order.
 *
 * Only the spans that row actually measured. The whole point of a per-turn
 * breakdown is to explain ONE slow turn, and nine rows of which six are em
 * dashes buries the two that say where the eleven seconds went.
 */
export function turnTiming(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  return TIMING_STAGES.map((stage) => ({
    ...stage,
    ms: measure(source[stage.key]),
  })).filter((stage) => stage.ms !== null);
}

/**
 * One `GET /api/v1/voice/turns` row, flattened for the template.
 *
 * The joined name fields are read at three depths on purpose. The contract says
 * the row carries the user's name and email and the lab's name, but not whether
 * the join lands them flat (`userName`) or as a populated document (`userId`
 * becoming an object), and both idioms exist in this backend. Reading all three
 * costs nothing and means the page does not break on whichever one ships.
 */
export function shapeTurn(raw) {
  const row = raw && typeof raw === "object" ? raw : {};
  const parse = (row.parse && typeof row.parse === "object" && row.parse) || {};
  const refusal = (row.refusal && typeof row.refusal === "object" && row.refusal) || {};
  const readback = (row.readback && typeof row.readback === "object" && row.readback) || {};
  const client = (row.client && typeof row.client === "object" && row.client) || {};
  const args = parse.args && typeof parse.args === "object" ? parse.args : null;

  return {
    id: idOf(row._id) || pick(row.turnId) || "",
    turnId: pick(row.turnId),
    at: pick(row.at) || (row.at ? String(row.at) : ""),
    kind: pick(row.kind) || "command",
    channel: pick(row.channel),
    labId: idOf(row.labId),
    userId: idOf(row.userId),
    userName: pick(row.userName, row.user && row.user.name, row.userId && row.userId.name),
    userEmail: pick(row.userEmail, row.user && row.user.email, row.userId && row.userId.email),
    labName: pick(row.labName, row.lab && row.lab.name, row.labId && row.labId.name),
    // Verbatim. The whole point of the collection is that this is what was
    // actually said, so it is never trimmed, cased or truncated here.
    transcript: typeof row.transcript === "string" ? row.transcript : "",
    source: pick(parse.source),
    tool: pick(parse.tool),
    args,
    argsSummary: compactArgs(args),
    confidence: measure(parse.confidence),
    latencyMs: measure(parse.latencyMs),
    usd: measure(parse.usd),
    grammarAbstained: parse.grammarAbstained === true,
    injectionFlag: parse.injectionFlag === true,
    // FIX-B9 item 1. `outcome` is what the parse DECIDED and `abstainCode` is
    // why it gave up; they are different vocabularies and neither substitutes
    // for the other. `abstainDetail` keeps the grammar's own sentence, which is
    // the thing that says WHICH number nothing explained.
    outcome: pick(parse.outcome),
    abstainCode: pick(parse.abstainCode),
    abstainDetail: pick(parse.abstainDetail),
    // Every arm consulted on this turn, in the order they were asked, so one
    // row reads "grammar abstained (UNCLAIMED_NUMBER) -> jev unsure (0.41) ->
    // gemini won in 1,412 ms". Shaped rather than passed through because a row
    // that is not an object would take the whole list down.
    arms: (Array.isArray(parse.arms) ? parse.arms : [])
      .filter((arm) => arm && typeof arm === "object")
      .map((arm) => ({
        arm: pick(arm.arm),
        outcome: pick(arm.outcome),
        code: pick(arm.code),
        latencyMs: measure(arm.latencyMs),
      })),
    // Jev's real answer: the intent distribution and the per-slot margins. Kept
    // as the server sent it — the expanded row renders it as JSON, and this
    // page has no business deciding which of a model's probabilities matter.
    jev: parse.jev && typeof parse.jev === "object" ? parse.jev : null,
    // The dock's own stopwatch for this turn (FIX-F10). Coerced through
    // `measure`, so a stage the client never reached stays absent rather than
    // becoming a confident zero on the row.
    timing: turnTiming(row.timing),
    refusalCode: pick(refusal.code),
    refusalReason: pick(refusal.reason),
    refused: !!pick(refusal.code),
    readbackText: typeof readback.text === "string" ? readback.text : "",
    readbackCached: readback.cached === true,
    receiptId: idOf(row.receiptId),
    previewHash: pick(row.previewHash),
    editedFrom: pick(row.editedFrom),
    micMode: pick(client.micMode),
    phone: client.phone === true,
    raw: row,
  };
}

export function shapeTurns(rows) {
  return (Array.isArray(rows) ? rows : []).map(shapeTurn);
}

/**
 * Read the row array out of whichever envelope the endpoint uses.
 *
 * The store's `voiceFetch` has already unwrapped the outer `data`, so what
 * arrives is either the array itself or a wrapper around it. Both the generic
 * controller factory (`data.data`) and the hand-written voice endpoints
 * (`data.turns`) exist in this backend, and guessing wrong shows an empty page
 * that reports no error at all — the most expensive kind of wrong.
 */
export function turnsFrom(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.turns)) return payload.turns;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.rows)) return payload.rows;
  return [];
}

/**
 * Append a page, dropping rows already held.
 *
 * `before` is a timestamp, not a cursor, so two turns sharing one millisecond
 * can be served twice across a page boundary. Order is preserved rather than
 * re-sorted: the server decides what "newest first" means, and a client that
 * re-sorted by its own parse of `at` would reorder rows it cannot tie-break.
 */
export function mergeTurns(existing, incoming) {
  const out = [];
  const seen = new Set();
  for (const row of [...(existing || []), ...(incoming || [])]) {
    if (!row) continue;
    const key = row.id || row.turnId || "";
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    out.push(row);
  }
  return out;
}

/**
 * The cursor for the next page: the server's answer when it gives one.
 *
 * `GET /api/v1/voice/turns` returns `nextBefore` itself — the `at` of its
 * oldest row, or null when the page was not full. That is authoritative and a
 * client-side guess is not, so it wins whenever the field is PRESENT.
 *
 * Presence, not truthiness, is the test. `nextBefore: null` is the server
 * saying "there is no more"; a missing `nextBefore` is a server that does not
 * speak this dialect, and the two must not collapse into the same branch — one
 * ends the paging correctly, the other would end it by accident.
 *
 * The echoed `limit` is preferred over the asked-for one in the fallback, since
 * the server clamps and only its number tells you whether a page was full.
 */
export function cursorFrom(payload, rows, limit) {
  if (payload && typeof payload === "object" && "nextBefore" in payload)
    return typeof payload.nextBefore === "string" && payload.nextBefore
      ? payload.nextBefore
      : null;
  const echoed = Number(payload && payload.limit);
  return nextBefore(rows, Number.isFinite(echoed) && echoed > 0 ? echoed : limit);
}

/**
 * The `before` for the next page worked out from the rows, for a server that
 * did not say. A short page means the end of the data; returning a cursor
 * anyway would leave a "Load more" button that fetches nothing forever.
 */
export function nextBefore(rows, limit) {
  const list = Array.isArray(rows) ? rows : [];
  const size = Number(limit);
  if (!Number.isFinite(size) || size <= 0) return null;
  if (list.length < size) return null;
  const last = list[list.length - 1];
  const at = last && (last.at || (last.raw && last.raw.at));
  return typeof at === "string" && at ? at : null;
}

// ---------------------------------------------------------------------------
// Query strings
// ---------------------------------------------------------------------------

export const DEFAULT_TURN_LIMIT = 50;
export const MAX_TURN_LIMIT = 500; // the server's own cap (FIX-B6 items 5 and 7)

function queryFrom(pairs) {
  const parts = pairs
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`);
  return parts.length ? `?${parts.join("&")}` : "";
}

export function overviewQuery(range) {
  return queryFrom([
    ["since", range && range.since],
    ["until", range && range.until],
  ]);
}

/**
 * The turns query.
 *
 * Empty filters are OMITTED, never sent empty. `labId=` is not "no lab filter"
 * to an endpoint that casts it to an ObjectId — it is a filter that matches
 * nothing, and the page would show an empty list with no explanation. The limit
 * is clamped here as well as on the server so a hand-edited value cannot ask for
 * a page the server will silently shrink, leaving `nextBefore` comparing
 * against a size that never arrives.
 */
export function turnsQuery(filters) {
  const f = filters && typeof filters === "object" ? filters : {};
  const asked = Number(f.limit);
  const limit = Number.isFinite(asked) && asked > 0
    ? Math.min(Math.floor(asked), MAX_TURN_LIMIT)
    : DEFAULT_TURN_LIMIT;

  return queryFrom([
    ["since", f.since],
    ["until", f.until],
    ["labId", f.labId],
    ["userId", f.userId],
    ["kind", f.kind],
    ["source", f.source],
    // FIX-B9 item 4. Omitted when empty for the same reason `labId` is: a
    // filter sent empty is a filter that matches nothing, on an endpoint that
    // would answer with a blank list and no explanation.
    ["abstainCode", f.abstainCode],
    ["outcome", f.outcome],
    // The literal `1` the contract names, and only when asked for. `shape=0` is
    // a third state the server was never asked to understand.
    ["shape", f.shape ? "1" : ""],
    // Only ever sent as the literal `true` the contract names. `refused=false`
    // is a third state the server was never asked to understand.
    ["refused", f.refusedOnly ? "true" : ""],
    ["q", typeof f.q === "string" ? f.q.trim() : ""],
    ["before", f.before],
    ["limit", limit],
  ]);
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const DASH = "—";

function group(intPart) {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** A count. Absent stays absent; this page never invents a zero. */
export function formatCount(value) {
  const n = Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(n))
    return DASH;
  const fixed = Math.round(n);
  return (fixed < 0 ? "-" : "") + group(String(Math.abs(fixed)));
}

/**
 * Money, at the scale this system actually spends it.
 *
 * A single parse costs fractions of a cent, so two decimals would round most
 * per-user and per-arm figures to `$0.00` — a number that reads as "free" and
 * is the reason nobody notices an arm quietly costing more than the rest.
 * Anything under a cent therefore keeps four decimals, and only a true zero is
 * allowed to print as `$0.00`.
 */
export function formatUsd(value) {
  const n = Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(n))
    return DASH;
  if (n === 0) return "$0.00";
  const abs = Math.abs(n);
  const decimals = abs < 0.01 ? 4 : 2;
  const [intPart, frac] = abs.toFixed(decimals).split(".");
  return `${n < 0 ? "-" : ""}$${group(intPart)}.${frac}`;
}

/** Audio seconds as minutes, one decimal. */
export function formatMinutes(seconds) {
  const n = Number(seconds);
  if (seconds === null || seconds === undefined || seconds === "" || !Number.isFinite(n))
    return DASH;
  const minutes = round1(n / 60);
  const [intPart, frac] = String(Math.abs(minutes)).split(".");
  return `${minutes < 0 ? "-" : ""}${group(intPart)}${frac ? `.${frac}` : ""}`;
}

export function formatMs(value) {
  const n = measure(value);
  if (n === null) return DASH;
  return `${formatCount(Math.round(n))} ms`;
}

export function formatPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DASH;
  return `${round1(n)}%`;
}

export function formatConfidence(value) {
  const n = measure(value);
  if (n === null) return "";
  return n.toFixed(2);
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * When something happened, in the form the reader needs.
 *
 * Inside a day the useful fact is how long ago; beyond that it is the date. The
 * absolute form is locale-formatted rather than ISO because this is a screen an
 * admin reads, not a log — and an unparseable value returns the empty string
 * rather than "Invalid Date", which is a string users have reported as a bug.
 */
export function formatWhen(value, now) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const at = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  const delta = at.getTime() - date.getTime();

  if (delta >= 0 && delta < MINUTE) return "just now";
  if (delta >= 0 && delta < HOUR) return `${Math.floor(delta / MINUTE)} min ago`;
  if (delta >= 0 && delta < DAY) return `${Math.floor(delta / HOUR)} h ago`;
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** The exact instant, for the title attribute and the expanded row. */
export function formatExact(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

// ---------------------------------------------------------------------------
// Empty states
// ---------------------------------------------------------------------------

/**
 * True when the range genuinely holds nothing.
 *
 * Checked on the arrays as well as on `turns`, because a lab that opened
 * sessions without completing a turn is not nothing — it is a lab that tried
 * and got no further, which is the single most interesting row on this page.
 */
export function isEmptyOverview(overview) {
  if (!overview || typeof overview !== "object") return true;
  const labs = Array.isArray(overview.labs) ? overview.labs : [];
  const users = Array.isArray(overview.users) ? overview.users : [];
  if (labs.length || users.length) return false;
  const totals = normaliseTotals(overview.totals);
  return totals.turns === 0 && totals.sessions === 0;
}

/** The whole overview, shaped once, so the page holds one object. */
export function shapeOverview(payload) {
  const source = payload && typeof payload === "object" ? payload : {};
  return {
    range: (source.range && typeof source.range === "object" && source.range) || {
      since: "",
      until: "",
    },
    totals: normaliseTotals(source.totals),
    labs: labRows(source),
    users: userRows(source),
    // The three FIX-B9 blocks, each with its own presence flag. PRESENCE, not
    // content: a server that has the feature and measured nothing in this range
    // says so honestly, and a server that predates the feature must make the
    // page say "not available yet" rather than draw nine empty bars — from
    // which a reader would conclude that every stage of voice is instant.
    stages: stageRows(source),
    stagesPresent: hasStages(source),
    abstains: abstainRows(source),
    abstainsPresent: hasAbstains(source),
    arms: armRows(source),
    armsPresent: hasArms(source),
    empty: isEmptyOverview(source),
  };
}
